import type { FeedbackFilters } from "@repo/api-client";

/**
 * The feedback board's words and its filters (0091). Kinds and statuses are
 * checked text on the wire, so their labels live here; the filters live in
 * the address (`?kind=&status=`, repeated), never in storage, and an address
 * naming none means the default: both kinds, and only what is still open.
 */

export const KINDS = [
  { value: "bug", label: "Bug" },
  { value: "idea", label: "Idea" },
] as const;

export const STATUSES = [
  { value: "new", label: "New" },
  { value: "planned", label: "Planned" },
  { value: "done", label: "Done" },
  { value: "wont_do", label: "Won't do" },
] as const;

/** What a form offers first: a bug is the likelier thing to be sent. */
export const DEFAULT_KIND = "bug";

/** The api's bound on a message, checked here first. */
export const MAX_FEEDBACK = 5000;

export function kindLabel(kind: string): string {
  return KINDS.find((known) => known.value === kind)?.label ?? kind;
}

export function statusLabel(status: string): string {
  return STATUSES.find((known) => known.value === status)?.label ?? status;
}

/** Both kinds on, and the statuses still open: Done and Won't do wait behind their chips. */
export const DEFAULT_FILTERS = {
  kind: KINDS.map((kind) => kind.value) as string[],
  status: ["new", "planned"] as string[],
} satisfies Required<FeedbackFilters>;

export type BoardFilters = typeof DEFAULT_FILTERS;

/** In the order the chips are drawn, so one set of filters is one key. */
function ordered(values: string[], known: readonly { value: string }[]): string[] {
  return known.map((option) => option.value).filter((value) => values.includes(value));
}

/**
 * The filters an address names. A group not in the address is the default; a
 * group in it with nothing on (`?kind=`) is none, which matches nothing — the
 * board asks the api nothing then, since the api reads a filter left out as
 * every one.
 */
export function filtersOf(search: string): BoardFilters {
  const params = new URLSearchParams(search);
  return {
    kind: params.has("kind") ? ordered(params.getAll("kind"), KINDS) : DEFAULT_FILTERS.kind,
    status: params.has("status") ? ordered(params.getAll("status"), STATUSES) : DEFAULT_FILTERS.status,
  };
}

/** The address's query for some filters: "" for the default, which needs none. */
export function searchOf(filters: BoardFilters): string {
  const params = new URLSearchParams();
  const write = (name: "kind" | "status", known: readonly { value: string }[]) => {
    const values = ordered(filters[name], known);
    if (values.join() === DEFAULT_FILTERS[name].join()) return;
    if (values.length === 0) params.append(name, "");
    for (const value of values) params.append(name, value);
  };
  write("kind", KINDS);
  write("status", STATUSES);
  const text = params.toString();
  return text ? `?${text}` : "";
}
